import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import FlexRow from '../../../../components/molecules/FlexRow'
import { useFormattingContext } from '../../../../components/organisms/FormattingProvider/FormattingContext'

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  usageContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '16px', // Maintain height even when empty
    cursor: 'pointer'
  },
  usage: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-neutral-400)',
    fontFamily: 'var(--font-sans), sans-serif'
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '4px 0'
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    fontSize: 'var(--font-size-xs)'
  },
  tooltipModel: {
    fontWeight: 500,
    color: 'var(--color-neutral-200)'
  },
  tooltipTokens: {
    color: 'var(--color-neutral-400)'
  }
}))

function ExplainabilityToolbar ({ events, usage }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()

  const { totalInput, totalOutput, modelBreakdown } = useMemo(() => {
    if (!usage || typeof usage !== 'object') {
      return { totalInput: 0, totalOutput: 0, modelBreakdown: [] }
    }

    let totalInput = 0
    let totalOutput = 0
    const modelBreakdown = []

    Object.entries(usage).forEach(([modelName, modelUsage]) => {
      if (modelUsage && typeof modelUsage === 'object') {
        const input = modelUsage.inputTokens || 0
        const output = modelUsage.outputTokens || 0
        totalInput += input
        totalOutput += output
        modelBreakdown.push({
          model: modelName,
          input,
          output
        })
      }
    })

    return { totalInput, totalOutput, modelBreakdown }
  }, [usage])

  const handleCopyClick = useCallback((e) => {
    e.stopPropagation()
    e.preventDefault()

    navigator.clipboard.writeText(JSON.stringify(events || [], null, 2)).catch(console.error)
  }, [events])

  const hasUsage = totalInput > 0 || totalOutput > 0
  const hasMultipleModels = modelBreakdown.length > 1
  const totalTokens = totalInput + totalOutput

  const usageDisplay = (
    <div className={classes.usageContainer}>
      <span className={classes.usage}>
        {formatter(totalTokens, 'human')} tokens
      </span>
    </div>
  )

  const wrappedUsageDisplay = hasUsage && hasMultipleModels ? (
    <Tooltip
      title={
        <div className={classes.tooltipContent}>
          {modelBreakdown.map(({ model, input, output }) => (
            <div key={model} className={classes.tooltipRow}>
              <span className={classes.tooltipModel}>{model}</span>
              <span className={classes.tooltipTokens}>
                {formatter(input, 'human')} input / {formatter(output, 'human')} output
              </span>
            </div>
          ))}
        </div>
      }
      arrow
      placement='top'
    >
      {usageDisplay}
    </Tooltip>
  ) : (
    usageDisplay
  )

  return (
    <FlexRow className={classes.toolbar}>
      {wrappedUsageDisplay}
      <span className='clickable font-size-xs' onClick={handleCopyClick}>Copy</span>
    </FlexRow>
  )
}

ExplainabilityToolbar.propTypes = {
  events: PropTypes.array,
  usage: PropTypes.object
}

export default ExplainabilityToolbar
